import {Suspense} from 'react'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {Link as PrimerLink} from '@primer/react'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {ownerPath} from '@github-ui/paths'
import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import type {AssigneesData, Assignee} from '../../page-data/payloads/assignees'
import {SidebarSection} from './SidebarSection'
import styles from './Assignees.module.css'
import {UserSectionSkeleton} from './UserSectionSkeleton'
import {useAssigneesQuery} from './data/route-queries'

export function Assignees() {
  return (
    <ErrorBoundary
      boundaryName="Assignees"
      fallback={
        <SidebarSection title="Assignees">
          <span className={styles.emptyState}>Unable to load assignees</span>
        </SidebarSection>
      }
    >
      <Suspense fallback={<UserSectionSkeleton title="Assignees" placeholderRows={1} />}>
        <AssigneesContent />
      </Suspense>
    </ErrorBoundary>
  )
}

function AssigneesContent() {
  const {data: assigneesData} = useAssigneesQuery()

  return <AssigneesComponent assigneesData={assigneesData} />
}

function AssigneesComponent({assigneesData}: {assigneesData: AssigneesData}) {
  const assignees = assigneesData.assignees ?? []

  if (assignees.length === 0) {
    return (
      <SidebarSection title="Assignees">
        <span className={styles.emptyState}>No one assigned</span>
      </SidebarSection>
    )
  }

  return (
    <SidebarSection title="Assignees">
      <ul className={styles.assigneeList}>
        {assignees.map(assignee => (
          <AssigneeRow key={assignee.id} assignee={assignee} />
        ))}
      </ul>
    </SidebarSection>
  )
}

function AssigneeRow({assignee}: {assignee: Assignee}) {
  const getHovercardAttributesForType = useGetHovercardAttributesForType()
  const hovercardAttrs = getHovercardAttributesForType('user', {login: assignee.displayLogin})

  return (
    <li className={styles.assigneeRow}>
      <PrimerLink
        href={ownerPath({owner: assignee.displayLogin})}
        muted
        className={styles.assigneeIdentity}
        {...hovercardAttrs}
      >
        <GitHubAvatar src={assignee.avatarUrl} size={20} />
        <span className={styles.assigneeName}>{assignee.displayLogin}</span>
      </PrimerLink>
    </li>
  )
}
